import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { PasswordInput } from "@/components/student/PasswordInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If we already have a valid session cookie, /me will succeed
    axios
      .get(`${API}/admin/me`, { withCredentials: true })
      .then(() => nav("/admin"))
      .catch(() => {});
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, form, {
        withCredentials: true,
      });
      localStorage.setItem("sp-admin-user", JSON.stringify(data.user));
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen grid place-items-center bg-[#F8FAFC] dark:bg-[#020617] px-6 gradient-mesh"
    >
      <div className="w-full max-w-md rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white/80 dark:bg-white/[0.04] glass p-8 hairline">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A]">
            <Lock className="h-4 w-4" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              SkillPreneur Academy
            </div>
            <div className="font-display text-xl tracking-tight font-semibold text-[#0F172A] dark:text-white">
              Admin Login
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="mt-7 space-y-3">
          <input
            data-testid="admin-email"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/50 text-[#0F172A] dark:text-white"
          />
          <PasswordInput
            testid="admin-password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/50 text-[#0F172A] dark:text-white"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex justify-center items-center gap-2 rounded-xl bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-6 py-3.5 text-sm font-medium disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-center">
            <Link
              to="/admin/forgot-password"
              data-testid="admin-forgot-link"
              className="text-xs font-semibold text-slate-500 hover:text-[#2563EB] dark:hover:text-[#06B6D4] transition-colors"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}
